/**
 * @fileoverview Department panel for the system-web department drawer and detail surfaces.
 * @module system-web/pages/DepartmentPanel
 *
 * Responsibilities:
 * - Render department tabs and read/edit views for local-first tenant data.
 * - Surface department-level trial lifecycle actions through parent-provided handlers.
 * - Auto-poll the server for provisioning completion when tenant.status is 'provisioning',
 *   writing the refreshed record into Dexie so the parent watchTenants liveQuery propagates
 *   the updated status back to this panel automatically.
 *
 * Dependencies:
 * - tenantService (retryProvisioning, refreshTenantFromServer, checkProvisioningStatus)
 * - ui-admin inline editing and audit log components
 *
 * Architecture:
 * - Local-first. This panel renders tenant data passed from service-backed page containers.
 * - The provisioning auto-poll is the one exception where this panel initiates a server read
 *   (via refreshTenantFromServer) — it is classified as an inherently-online reconciliation
 *   operation for a transient, time-sensitive server state that background replication may
 *   not pick up promptly.
 * - Parent pages reconcile lifecycle changes through tenantService after optimistic local updates.
 *
 * Notes:
 * - Trial lifecycle actions must remain delegated to parent pages so shared service orchestration stays centralized.
 * - The provisioning poll clears itself as soon as tenant.status transitions away from 'provisioning'.
 */
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { DepartmentCodeSetsTab } from './DepartmentCodeSetsTab';
import { DepartmentDocumentsTab } from './DepartmentDocumentsTab';
import DepartmentMappingTab from './DepartmentMappingTab';
import DepartmentParcelDataTab from './DepartmentParcelDataTab';
import { useTheme } from '@ui-admin/contexts/ThemeContext';
import {
  MapPin, CreditCard, BarChart3, Globe, Users, Shield, Settings, Palette,
  Database, FileText, Bell, Map, Clock, Sparkles, Phone, Mail, Star,
  ChevronDown, ChevronRight, Pencil, Trash2, Plus, UserPlus, X, ScrollText,
  Building2, Flame, AlertCircle, Loader2, RefreshCw,
} from 'lucide-react';
import { listModules } from '../services/moduleService';
import { canEnableModuleSelection, canonicalizeTenantModuleCode } from '@firesquirrel/core-domain';
import { fetchAuditLogs } from '../services/systemAuditLogService';
import { retryProvisioning, checkProvisioningStatus, retryRegridImport } from '../services/tenantService';
import { resolveClusterName } from '../services/clusterService';
import { InlineEditableField } from '@ui-admin/components/InlineEditableField';
import type { InlineEditFieldConfig } from '@ui-admin/components/InlineEditableField';
import ConfirmActionModal from '../components/ui/ConfirmActionModal';
import DepartmentLogoUploadField from '../components/DepartmentLogoUploadField';
import AuditLogTimeline from '@ui-admin/components/AuditLogTimeline';
import ValidatedEmailInput from '../components/ValidatedEmailInput';
import JurisdictionStateCountyField from '../components/JurisdictionStateCountyField';
import JurisdictionCountryField from '../components/JurisdictionCountryField';
import { formatPhone } from '../utils/phoneFormatting';
import { validateZip, validateWebsite } from '../utils/fieldValidation';
import DepartmentAuthManagementCard from '../components/auth-setup/DepartmentAuthManagementCard';
import DepartmentAuthSetupWizard from '../components/auth-setup/DepartmentAuthSetupWizard';

/**
 * @localFirst
 *
 * Source of Truth:
 * - local
 *
 * Write Flow:
 * - The panel renders local tenant state and delegates writes to parent callbacks backed by local-first services.
 *
 * Sync Strategy:
 * - Parent pages reconcile lifecycle changes through tenantService after optimistic local updates.
 *
 * Conflict Resolution:
 * - Parent service reconciliation determines the final tenant state shown in the panel.
 */

/**
 * @errorHandling
 *
 * Strategy:
 * - Non-trivial action failures are surfaced by parent pages while the panel keeps rendering the current local state.
 *
 * User Impact:
 * - Operators can remain in the panel and retry lifecycle actions.
 *
 * Logging:
 * - REQUIRED
 */

// ============================================================
// Types
// ============================================================

// ============================================================
// Types
// ============================================================

/**
 * @contract PanelTab
 *
 * Purpose:
 * - Defines the supported department panel tabs.
 *
 * Fields:
 * - overview: Summary and identity data.
 * - billing: Subscription and commercial information.
 * - config: Department configuration.
 * - mapping: Jurisdiction mapping data.
 * - modules: Module and provisioning metadata.
 * - contacts: Department contacts.
 * - codeSets: Code set configuration.
 * - trial: Trial lifecycle and onboarding state.
 * - audit: Audit timeline.
 * - parcelData: Regrid parcel data management (source file, stats, tokens, history).
 *
 * Rules:
 * - Parent containers control the active tab state.
 *
 * Sync:
 * - UI-only contract; underlying data comes from local Dexie-backed tenant state.
 */
export type PanelTab = 'overview' | 'billing' | 'config' | 'mapping' | 'modules' | 'contacts' | 'codeSets' | 'trial' | 'audit' | 'documents' | 'parcelData';

/**
 * @contract DepartmentPanelProps
 *
 * Purpose:
 * - Defines the data and lifecycle handlers required to render the department panel.
 *
 * Fields:
 * - tenant: Current department record.
 * - activeTab: Selected tab.
 * - onTabChange: Tab selection handler.
 * - onSave: Local-first field persistence handler.
 * - onExtend: Shared trial extension action.
 * - onConvert: Shared trial-to-active activation action.
 * - onCleanup: Trial cleanup action.
 * - onMarkAsTrial: Optional action to place a department on trial.
 *
 * Rules:
 * - Trial lifecycle handlers must be service-backed in the parent container.
 *
 * Sync:
 * - Props reflect local-first tenant state held by the parent page.
 */
export interface DepartmentPanelProps {
  tenant: any;
  activeTab: PanelTab;
  onTabChange: (tab: PanelTab) => void;
  onSave: (fieldKey: string, value: unknown) => Promise<void>;
  onExtend: (id: string, name: string) => void;
  onConvert: (id: string, name: string) => void;
  onCleanup: (id: string, name: string) => void;
  onMarkAsTrial?: (id: string, name: string) => void;
}

// ============================================================
// Helpers
// ============================================================

const TIER_COLORS: Record<string, string> = {
  professional: 'bg-blue-100 text-blue-700',
  advanced: 'bg-purple-100 text-purple-700',
  premium: 'bg-amber-100 text-amber-700',
};

const STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-red-100 text-red-700',
  demo: 'bg-yellow-100 text-yellow-700',
  expired: 'bg-gray-100 text-gray-600',
};

function cfg(fieldKey: string, editType: InlineEditFieldConfig['editType'], rest?: Partial<InlineEditFieldConfig>): InlineEditFieldConfig {
  return { fieldKey, editType, ...rest };
}


function ReadField({ label, value, mono, span2, textPrimary, textSecondary }: { label: string; value?: any; mono?: boolean; span2?: boolean; textPrimary?: string; textSecondary?: string }) {
  const display = value === null || value === undefined || value === '' ? '—' : String(value);
  return (
    <div className={span2 ? 'col-span-2' : ''}>
      <dt className="text-xs font-medium mb-0.5" style={{ color: textSecondary }}>{label}</dt>
      <dd className={`text-sm break-all ${mono ? 'font-mono text-xs' : ''}`} style={{ color: textPrimary }}>{display}</dd>
    </div>
  );
}

function PanelSection({ title, icon: Icon, children, expandedBg, textSecondary }: { title: string; icon: any; children: React.ReactNode; expandedBg?: string; textSecondary?: string }) {
  return (
    <div className="mb-5">
      <div className="flex items-center gap-1.5 mb-2">
        <Icon className="h-3.5 w-3.5" style={{ color: textSecondary }} />
        <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: textSecondary }}>{title}</h4>
      </div>
      <div className="rounded-lg p-3" style={{ background: expandedBg }}>
        {children}
      </div>
    </div>
  );
}

// ============================================================
// Contact types
// ============================================================

export interface DeptContact {
  _id: string;
  name: string;
  title?: string;
  isPrimary: boolean;
  emails: Array<{ type: string; address: string; isPrimary: boolean }>;
  phones: Array<{ type: string; number: string; isPrimary: boolean; extension?: string }>;
  notes?: string;
}

function emptyContact(): DeptContact {
  return {
    _id: crypto.randomUUID(),
    name: '',
    title: '',
    isPrimary: false,
    emails: [{ type: 'work', address: '', isPrimary: true }],
    phones: [{ type: 'work', number: '', isPrimary: true }],
    notes: '',
  };
}

/**
 * @reusable channelTypeLabel
 * Usage:   Internal — normalize contact channel `type` fields to a display label.
 *          Stored values may be a plain string ('work') or a LookupReference
 *          object ({ lookupId, lookupType, code, name }).
 * Rules:   presentation-only — never throws on unexpected input.
 */
function channelTypeLabel(t: any): string {
  if (typeof t === 'string') return t;
  if (t && typeof t === 'object') return String(t.name || t.code || '');
  return '';
}

/**
 * @process normalizeDeptContact
 *
 * Steps:
 * 1. Map any stored tenant contact shape into the panel's DeptContact view
 *    model — canonical TenantContact (phoneNumbers[] / emails[].email /
 *    isPrimaryContact), wizard shape (phones[] / emails[].address /
 *    isPrimary), or legacy scalar { email, phone }.
 * 2. Guarantee emails[] and phones[] are always arrays so card rendering
 *    never calls .find/.map on undefined.
 *
 * Inputs:
 * - raw: stored tenant.contacts[] entry (any shape).
 * - index: position fallback for entries missing _id (stable across renders).
 *
 * Outputs:
 * - DeptContact — normalized view model.
 *
 * Failure Handling:
 * - Missing/unknown channel collections produce empty arrays (no crash).
 *
 * Idempotency: REQUIRED — pure function.
 */
export function normalizeDeptContact(raw: any, index: number): DeptContact {
  const rawPhones = Array.isArray(raw?.phones)
    ? raw.phones
    : Array.isArray(raw?.phoneNumbers)
      ? raw.phoneNumbers
      : [];
  const emails = (Array.isArray(raw?.emails) ? raw.emails : []).map((e: any) => ({
    type: channelTypeLabel(e?.type) || 'work',
    address: e?.address ?? e?.email ?? '',
    isPrimary: Boolean(e?.isPrimary),
  }));
  const phones = rawPhones.map((p: any) => ({
    type: channelTypeLabel(p?.type) || 'work',
    number: p?.number ?? p?.phone ?? '',
    isPrimary: Boolean(p?.isPrimary),
    ...(p?.extension ? { extension: p.extension } : {}),
  }));
  if (emails.length === 0 && typeof raw?.email === 'string' && raw.email) {
    emails.push({ type: 'work', address: raw.email, isPrimary: true });
  }
  if (phones.length === 0 && typeof raw?.phone === 'string' && raw.phone) {
    phones.push({ type: 'work', number: raw.phone, isPrimary: true });
  }
  return {
    _id: String(raw?._id ?? `contact-${index}`),
    name: raw?.name ?? [raw?.firstName, raw?.lastName].filter(Boolean).join(' '),
    title: raw?.title,
    isPrimary: Boolean(raw?.isPrimary ?? raw?.isPrimaryContact),
    emails,
    phones,
    notes: raw?.notes,
  };
}

/**
 * Merge one edited channel entry back onto its stored counterpart.
 * Preserves unmodeled fields (_id, countryCode, extra keys) via spread and
 * keeps an object-typed `type` LookupReference when the label is unchanged.
 * valueField selects the stored value key: 'email' (canonical),
 * 'address' (wizard shape), or 'number' (phones).
 */
function toStoredChannel(
  e: { type: string; address?: string; number?: string; extension?: string; isPrimary: boolean },
  orig: any,
  valueField: 'email' | 'address' | 'number',
): Record<string, any> {
  const keepType = orig && typeof orig.type === 'object' && channelTypeLabel(orig.type) === e.type;
  const out: Record<string, any> = {
    ...(orig ?? {}),
    type: keepType ? orig.type : e.type,
    isPrimary: e.isPrimary,
  };
  delete out.email;
  delete out.address;
  delete out.number;
  delete out.phone;
  if (valueField === 'number') {
    out.number = e.number ?? '';
    if (e.extension !== undefined) out.extension = e.extension;
    else delete out.extension;
  } else {
    out[valueField] = e.address ?? '';
  }
  return out;
}

/**
 * @process toStoredContact
 *
 * Steps:
 * 1. Merge the edited DeptContact view-model onto the original stored doc so
 *    unmodeled fields (status, affiliation, createdAt) survive the save.
 * 2. Emit the canonical TenantContact field set (phoneNumbers[] /
 *    emails[].email / isPrimaryContact) when the original is canonical or new,
 *    otherwise keep the wizard field set (phones[] / emails[].address /
 *    isPrimary) so existing docs are not silently migrated.
 *
 * Inputs:
 * - c: DeptContact view-model from the contacts tab.
 * - original: matching raw tenant.contacts[] entry (by _id), if any.
 *
 * Outputs:
 * - Record<string, any> — stored-shape contact for onSave('contacts', ...).
 *
 * Failure Handling:
 * - New contacts (no original) are written in canonical shape.
 *
 * Idempotency: REQUIRED — pure function.
 */
export function toStoredContact(c: DeptContact, original?: any): Record<string, any> {
  const now = new Date().toISOString();
  const isCanonical =
    !original ||
    'phoneNumbers' in original ||
    'isPrimaryContact' in original ||
    (Array.isArray(original.emails) && original.emails.some((e: any) => e && 'email' in e));
  const next: Record<string, any> = {
    ...original,
    _id: original?._id ?? c._id,
    name: c.name,
    title: c.title,
    notes: c.notes,
    updatedAt: now,
    ...(original ? {} : { createdAt: now }),
  };
  if (isCanonical) {
    next.isPrimaryContact = c.isPrimary;
    delete next.isPrimary;
    delete next.phones;
    next.emails = c.emails.map((e, i) => toStoredChannel(e, original?.emails?.[i], 'email'));
    next.phoneNumbers = c.phones.map((p, i) => toStoredChannel(p, original?.phoneNumbers?.[i], 'number'));
  } else {
    next.isPrimary = c.isPrimary;
    delete next.isPrimaryContact;
    delete next.phoneNumbers;
    next.emails = c.emails.map((e, i) => toStoredChannel(e, original?.emails?.[i], 'address'));
    next.phones = c.phones.map((p, i) => toStoredChannel(p, original?.phones?.[i], 'number'));
  }
  return next;
}

/**
 * @process buildToggledModules
 *
 * Steps:
 * 1. Normalize stored module entries (string codes or objects).
 * 2. Flip isEnabled on an existing entry — preserving moduleId/name — and
 *    heal stub entries by backfilling moduleId/name from the catalog.
 * 3. When the code is absent, append a canonical TenantModule-shaped entry
 *    ({ moduleId, code, name, isEnabled, enabledAt }) so saved arrays match
 *    the shape written by resolveTenantModules at provisioning time.
 *
 * Inputs:
 * - modules: raw tenant.modules array.
 * - code: module code being toggled.
 * - enabled: target state.
 * - catalogEntry: matching system module catalog doc (may be undefined for
 *   legacy codes).
 *
 * Outputs:
 * - any[] — next tenant.modules array.
 *
 * Idempotency: REQUIRED — pure function.
 */
export function buildToggledModules(modules: any[], code: string, enabled: boolean, catalogEntry?: any): any[] {
  const normalized = (modules ?? [])
    .filter((entry) => entry && (typeof entry === 'string' || entry.code))
    .map((entry) => (
      typeof entry === 'string'
        ? { code: entry, isEnabled: true }
        : { ...entry, code: entry.code, isEnabled: entry.isEnabled !== false }
    ));
  // Match by canonical code so a drifted stored entry (e.g. 'wildlandFire')
  // heals to the canonical catalog code on the next toggle instead of
  // appending a second entry for the same semantic module.
  const canonicalTarget = canonicalizeTenantModuleCode(code);
  const idx = normalized.findIndex((m) => canonicalizeTenantModuleCode(m.code) === canonicalTarget);
  if (idx >= 0) {
    normalized[idx] = {
      ...normalized[idx],
      moduleId: catalogEntry?._id ?? normalized[idx].moduleId,
      code,
      name: normalized[idx].name ?? catalogEntry?.name ?? code,
      isEnabled: enabled,
    };
    if (enabled) normalized[idx].enabledAt = new Date().toISOString();
    else delete normalized[idx].enabledAt;
    // Drop any other entries that collapse to the same canonical module —
    // duplicate spellings of one module must never coexist on the tenant.
    return normalized.filter((m, i) => i === idx || canonicalizeTenantModuleCode(m.code) !== canonicalTarget);
  }
  normalized.push({
    moduleId: catalogEntry?._id ?? code,
    code,
    name: catalogEntry?.name ?? code,
    isEnabled: enabled,
    ...(enabled ? { enabledAt: new Date().toISOString() } : {}),
  });
  return normalized;
}

// ============================================================
// ContactEditForm — inline form for add/edit
// ============================================================

function ContactEditForm({ initial, onSubmit, onCancel, theme: t }: {
  initial: DeptContact;
  onSubmit: (c: DeptContact) => void;
  onCancel: () => void;
  theme: any;
}) {
  const [form, setForm] = useState<DeptContact>({ ...initial });

  const updatePhone = (idx: number, patch: Partial<DeptContact['phones'][0]>) => {
    const phones = form.phones.map((p, i) => i === idx ? { ...p, ...patch } : p);
    setForm(f => ({ ...f, phones }));
  };
  const addPhone = () => setForm(f => ({ ...f, phones: [...f.phones, { type: 'work', number: '', isPrimary: false }] }));
  const removePhone = (idx: number) => setForm(f => ({ ...f, phones: f.phones.filter((_, i) => i !== idx) }));

  const updateEmail = (idx: number, patch: Partial<DeptContact['emails'][0]>) => {
    const emails = form.emails.map((e, i) => i === idx ? { ...e, ...patch } : e);
    setForm(f => ({ ...f, emails }));
  };
  const addEmail = () => setForm(f => ({ ...f, emails: [...f.emails, { type: 'work', address: '', isPrimary: false }] }));
  const removeEmail = (idx: number) => setForm(f => ({ ...f, emails: f.emails.filter((_, i) => i !== idx) }));

  return (
    <div className="rounded-lg border p-3 space-y-3" style={{ borderColor: t.accent + '40', background: t.expandedBg }}>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: t.textSecondary }}>Name *</label>
          <input type="text" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500"
            placeholder="Full name" />
        </div>
        <div>
          <label className="block text-xs font-medium mb-1" style={{ color: t.textSecondary }}>Title</label>
          <input type="text" value={form.title || ''} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500"
            placeholder="Job title" />
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input type="checkbox" id={`primary-${form._id}`} checked={form.isPrimary} onChange={e => setForm(f => ({ ...f, isPrimary: e.target.checked }))} className="rounded" />
        <label htmlFor={`primary-${form._id}`} className="text-xs" style={{ color: t.textSecondary }}>Primary contact</label>
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium" style={{ color: t.textSecondary }}>Phones</span>
          <button type="button" onClick={addPhone} className="text-xs" style={{ color: t.accent }}>+ Add</button>
        </div>
        {form.phones.map((ph, i) => (
          <div key={i} className="flex items-center gap-1.5 mb-1.5">
            <select value={ph.type} onChange={e => updatePhone(i, { type: e.target.value })}
              className="w-20 px-1.5 py-1 text-xs border border-gray-300 rounded">
              <option value="work">Work</option>
              <option value="mobile">Mobile</option>
              <option value="fax">Fax</option>
            </select>
            <input type="tel" value={ph.number} onChange={e => updatePhone(i, { number: formatPhone(e.target.value) })}
              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
              placeholder="(555) 123-4567" />
            {form.phones.length > 1 && (
              <button type="button" onClick={() => removePhone(i)} className="text-gray-400 hover:text-red-500">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium" style={{ color: t.textSecondary }}>Emails</span>
          <button type="button" onClick={addEmail} className="text-xs" style={{ color: t.accent }}>+ Add</button>
        </div>
        {form.emails.map((em, i) => (
          <div key={i} className="flex items-center gap-1.5 mb-1.5">
            <select value={em.type} onChange={e => updateEmail(i, { type: e.target.value })}
              className="w-20 px-1.5 py-1 text-xs border border-gray-300 rounded">
              <option value="work">Work</option>
              <option value="personal">Personal</option>
            </select>
            <ValidatedEmailInput value={em.address} onChange={(v) => updateEmail(i, { address: v })}
              className="flex-1 px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-orange-500 pr-7"
              placeholder="email@example.com" />
            {form.emails.length > 1 && (
              <button type="button" onClick={() => removeEmail(i)} className="text-gray-400 hover:text-red-500">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        ))}
      </div>

      <div>
        <label className="block text-xs font-medium mb-1" style={{ color: t.textSecondary }}>Notes</label>
        <textarea rows={2} value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
          className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-orange-500" />
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button type="button" onClick={onCancel}
          className="px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
          style={{ borderColor: t.cardBorder, color: t.textSecondary }}>
          Cancel
        </button>
        <button type="button" onClick={() => { if (form.name.trim()) onSubmit(form); }}
          className="px-3 py-1.5 text-xs font-medium text-white rounded-lg transition-colors"
          style={{ background: t.accent }}>
          Save
        </button>
      </div>
    </div>
  );
}

// ============================================================
// DepartmentContactsTab
// ============================================================

function DepartmentContactsTab({ contacts, onContactsChange, theme: t }: {
  contacts: DeptContact[];
  onContactsChange: (updated: DeptContact[]) => void;
  theme: any;
}) {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [removeTarget, setRemoveTarget] = useState<DeptContact | null>(null);

  const primaryPhone = (c: DeptContact) => c.phones.find(p => p.isPrimary) || c.phones[0];
  const primaryEmail = (c: DeptContact) => c.emails.find(e => e.isPrimary) || c.emails[0];

  const handleAdd = (newContact: DeptContact) => {
    onContactsChange([...contacts, newContact]);
    setShowAddForm(false);
  };

  const handleEdit = (updated: DeptContact) => {
    onContactsChange(contacts.map(c => c._id === updated._id ? updated : c));
    setEditingId(null);
  };

  const handleRemove = (id: string) => {
    onContactsChange(contacts.filter(c => c._id !== id));
    setRemoveTarget(null);
    if (expandedId === id) setExpandedId(null);
  };

  const handleInlineSave = (contactId: string, fieldKey: string, newValue: unknown) => {
    const val = newValue as string;
    const updated = contacts.map(c => {
      if (c._id !== contactId) return c;
      const clone = { ...c };
      if (fieldKey === 'name') clone.name = val;
      else if (fieldKey === 'title') clone.title = val || undefined;
      else if (fieldKey === 'notes') clone.notes = val || undefined;
      else if (fieldKey.startsWith('phone.')) {
        const phoneIdx = parseInt(fieldKey.replace('phone.', ''), 10);
        clone.phones = clone.phones.map((p, i) => i === phoneIdx ? { ...p, number: val } : p);
      } else if (fieldKey.startsWith('email.')) {
        const emailIdx = parseInt(fieldKey.replace('email.', ''), 10);
        clone.emails = clone.emails.map((e, i) => i === emailIdx ? { ...e, address: val } : e);
      }
      return clone;
    });
    onContactsChange(updated);
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: t.textMuted }}>
          {contacts.length} Contact{contacts.length !== 1 ? 's' : ''}
        </span>
        <button
          onClick={() => { setShowAddForm(prev => !prev); setEditingId(null); }}
          className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg transition-colors"
          style={{
            background: showAddForm ? t.accent : 'transparent',
            color: showAddForm ? '#fff' : t.accent,
            border: `1px solid ${showAddForm ? t.accent : t.accent + '40'}`,
          }}>
          {showAddForm ? <X className="h-3 w-3" /> : <UserPlus className="h-3 w-3" />}
          {showAddForm ? 'Cancel' : 'Add Contact'}
        </button>
      </div>

      {/* Add form */}
      {showAddForm && (
        <ContactEditForm
          initial={emptyContact()}
          onSubmit={handleAdd}
          onCancel={() => setShowAddForm(false)}
          theme={t}
        />
      )}

      {/* Empty state */}
      {contacts.length === 0 && !showAddForm && (
        <div className="text-center py-10">
          <p className="text-sm" style={{ color: t.textMuted }}>No contacts on record</p>
          <button onClick={() => setShowAddForm(true)}
            className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors"
            style={{ borderColor: t.accent + '40', color: t.accent }}>
            <UserPlus className="h-3 w-3" /> Add a Contact
          </button>
        </div>
      )}

      {/* Contact cards */}
      {contacts.map((c) => {
        const isExpanded = expandedId === c._id;
        const pPhone = primaryPhone(c);
        const pEmail = primaryEmail(c);

        if (editingId === c._id) {
          return (
            <ContactEditForm
              key={c._id}
              initial={c}
              onSubmit={handleEdit}
              onCancel={() => setEditingId(null)}
              theme={t}
            />
          );
        }

        return (
          <div key={c._id} className="rounded-lg border overflow-hidden" style={{ borderColor: t.expandedCardBorder }}>
            <div
              className="px-2.5 py-2 cursor-pointer select-none"
              style={{ background: t.cardBg }}
              onClick={() => setExpandedId(prev => prev === c._id ? null : c._id)}
            >
              {/* Top row: badges + actions */}
              <div className="flex items-start gap-1 mb-1">
                <div className="flex flex-wrap gap-1 flex-1 min-w-0">
                  {c.isPrimary && (
                    <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-700">
                      <Star className="h-2.5 w-2.5" fill="currentColor" /> Primary
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <button
                    onClick={e => { e.stopPropagation(); setEditingId(c._id); setExpandedId(null); }}
                    className="p-0.5 rounded text-gray-300 hover:text-orange-500 hover:bg-orange-50 transition-colors"
                    title="Edit contact">
                    <Pencil className="h-3 w-3" />
                  </button>
                  <button
                    onClick={e => { e.stopPropagation(); setRemoveTarget(c); }}
                    className="p-0.5 rounded text-gray-300 hover:text-red-500 hover:bg-red-50 transition-colors"
                    title="Remove contact">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              </div>

              {/* Name row with chevron */}
              <div className="flex items-center gap-1.5">
                {isExpanded
                  ? <ChevronDown className="h-3 w-3 shrink-0" style={{ color: t.textMuted }} />
                  : <ChevronRight className="h-3 w-3 shrink-0" style={{ color: t.textMuted }} />}
                <div className="text-sm font-medium" style={{ color: t.textPrimary }}>{c.name}</div>
              </div>
              {c.title && (
                <div className="text-xs mt-0.5 pl-[18px]" style={{ color: t.textSecondary }}>{c.title}</div>
              )}

              {/* Collapsed: primary phone + email */}
              {!isExpanded && (pPhone || pEmail) && (
                <div className="grid grid-cols-1 gap-0.5 mt-1.5 pl-[18px] text-xs">
                  {pPhone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-3 w-3 shrink-0" style={{ color: t.textMuted }} />
                      <span style={{ color: t.accent }}>{pPhone.number || '—'}</span>
                    </div>
                  )}
                  {pEmail && (
                    <div className="flex items-center gap-2">
                      <Mail className="h-3 w-3 shrink-0" style={{ color: t.textMuted }} />
                      <span className="truncate" style={{ color: t.accent }}>{pEmail.address || '—'}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Expanded: inline-editable fields */}
              {isExpanded && (
                <div className="mt-2 pl-[18px] space-y-1.5" onClick={e => e.stopPropagation()}>
                  <InlineEditableField label="Name" value={c.name} config={{ fieldKey: 'name', editType: 'text', validate: v => (v as string)?.trim() ? null : 'Required' }} onSave={(fk, v) => handleInlineSave(c._id, fk, v)} />
                  <InlineEditableField label="Title" value={c.title || ''} config={{ fieldKey: 'title', editType: 'text', placeholder: 'Title' }} onSave={(fk, v) => handleInlineSave(c._id, fk, v)} />

                  {/* Phones */}
                  <div className="space-y-1">
                    {c.phones.map((ph, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Phone className="h-3 w-3 shrink-0" style={{ color: t.textMuted }} />
                        <InlineEditableField label="" value={ph.number || '—'} config={{ fieldKey: `phone.${i}`, editType: 'text' }} onSave={(fk, v) => handleInlineSave(c._id, fk, v)} className="inline" />
                        <span className="text-[9px]" style={{ color: t.textMuted }}>{ph.type}{ph.isPrimary && c.phones.length > 1 ? ' · primary' : ''}</span>
                      </div>
                    ))}
                  </div>

                  {/* Emails */}
                  <div className="space-y-1">
                    {c.emails.map((em, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Mail className="h-3 w-3 shrink-0" style={{ color: t.textMuted }} />
                        <InlineEditableField label="" value={em.address || '—'} config={{ fieldKey: `email.${i}`, editType: 'text' }} onSave={(fk, v) => handleInlineSave(c._id, fk, v)} className="inline" />
                        <span className="text-[9px]" style={{ color: t.textMuted }}>{em.type}{em.isPrimary && c.emails.length > 1 ? ' · primary' : ''}</span>
                      </div>
                    ))}
                  </div>

                  {/* Notes */}
                  {(c.notes !== undefined) && (
                    <InlineEditableField label="Notes" value={c.notes || ''} config={{ fieldKey: 'notes', editType: 'textarea', placeholder: 'Add notes...' }} onSave={(fk, v) => handleInlineSave(c._id, fk, v)} />
                  )}

                  {/* Add phone/email buttons */}
                  <div className="flex items-center gap-3 pt-1">
                    <button type="button"
                      onClick={() => {
                        const updated = contacts.map(ct => ct._id === c._id
                          ? { ...ct, phones: [...ct.phones, { type: 'work', number: '', isPrimary: false }] }
                          : ct);
                        onContactsChange(updated);
                      }}
                      className="inline-flex items-center gap-1 text-[10px]" style={{ color: t.accent }}>
                      <Plus className="h-2.5 w-2.5" /> Phone
                    </button>
                    <button type="button"
                      onClick={() => {
                        const updated = contacts.map(ct => ct._id === c._id
                          ? { ...ct, emails: [...ct.emails, { type: 'work', address: '', isPrimary: false }] }
                          : ct);
                        onContactsChange(updated);
                      }}
                      className="inline-flex items-center gap-1 text-[10px]" style={{ color: t.accent }}>
                      <Plus className="h-2.5 w-2.5" /> Email
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {removeTarget && (
        <ConfirmActionModal
          title="Remove Contact?"
          description={<>Remove <strong>{removeTarget.name}</strong> from this department?</>}
          confirmButtonLabel="Yes, Remove"
          variant="warning"
          onConfirm={() => handleRemove(removeTarget._id)}
          onClose={() => setRemoveTarget(null)}
        />
      )}
    </div>
  );
}

// ============================================================
// Tab definitions
// ============================================================

const ALL_TABS: { id: PanelTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'billing', label: 'Billing & Plan' },
  { id: 'config', label: 'Configuration' },
  { id: 'mapping', label: 'Mapping' },
  { id: 'modules', label: 'Modules' },
  { id: 'contacts', label: 'Contacts' },
  { id: 'codeSets', label: 'Code Sets' },
  { id: 'documents', label: 'Documents' },
  { id: 'parcelData', label: 'Parcel Data' },
  { id: 'trial', label: 'Trial' },
  { id: 'audit', label: 'Audit Log' },
];

// ============================================================
// DepartmentPanel
// ============================================================

/**
 * @component DepartmentPanel
 *
 * Purpose:
 * - Render the reusable department drawer/detail panel used across system-web department management surfaces.
 *
 * Props:
 * - tenant: Department record to render.
 * - activeTab: Currently selected tab.
 * - onTabChange: Tab change callback.
 * - onSave: Inline save callback.
 * - onExtend: Trial extension callback.
 * - onConvert: Department activation callback.
 * - onCleanup: Trial cleanup callback.
 * - onMarkAsTrial: Optional callback for placing a department on trial.
 *
 * Behavior:
 * - Renders department state, inline edits, audit history, and department-level trial controls.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Must not introduce direct tenant lifecycle API calls for extend/activate flows.
 *
 * Performance:
 * - Keeps rendering scoped to the selected tab to reduce drawer work.
 */
export default function DepartmentPanel({
  tenant,
  activeTab,
  onTabChange,
  onSave,
  onExtend,
  onConvert,
  onCleanup,
  onMarkAsTrial,
}: DepartmentPanelProps) {
  const { theme } = useTheme();
  const addr = tenant.address || {};
  const billing = tenant.billing || {};
  const sub = billing.subscription || {};
  const pricing = billing.pricing || {};
  const licensing = tenant.licensing || {};
  const brand = tenant.branding || {};
  const plan = tenant.inspectionPlan || {};
  const reg = tenant.regulatoryFramework || {};
  const legacy = tenant.legacySystem || {};
  const retention = tenant.dataRetentionPolicy || {};
  const notif = tenant.notificationPreferences || {};
  const onboarding = tenant.onboarding || {};
  const trial = tenant.trial || {};
  const jurisdiction = tenant.jurisdiction || {};
  const rawContacts: any[] = Array.isArray(tenant.contacts) ? tenant.contacts : [];
  const contacts: DeptContact[] = rawContacts.map(normalizeDeptContact);
  const modules: any[] = tenant.modules || [];
  const documents: any[] = tenant.documents || [];
  const [moduleCatalog, setModuleCatalog] = useState<any[]>([]);
  const [moduleCatalogLoading, setModuleCatalogLoading] = useState(true);
  const [moduleCatalogError, setModuleCatalogError] = useState<string | null>(null);
  const [authWizardOpen, setAuthWizardOpen] = useState(false);
  // Resolved cluster name — looked up from the clusters registry by clusterId
  // (authoritative) with fallback to the denormalized tenant.dataLocation.clusterName.
  const [clusterName, setClusterName] = useState<string | undefined>(tenant?.dataLocation?.clusterName);

  useEffect(() => {
    let cancelled = false;
    resolveClusterName(tenant)
      .then((name) => { if (!cancelled) setClusterName(name); })
      .catch(() => { if (!cancelled) setClusterName(tenant?.dataLocation?.clusterName); });
    return () => { cancelled = true; };
  }, [tenant?.dataLocation?.clusterId, tenant?.dataLocation?.clusterName]);

  useEffect(() => {
    let cancelled = false;

    setModuleCatalogLoading(true);
    listModules()
      .then((items) => {
        if (cancelled) return;
        setModuleCatalog((items || []).filter((m: any) => !m._deleted && !!m.code));
        setModuleCatalogError(null);
      })
      .catch((err: any) => {
        if (cancelled) return;
        setModuleCatalog([]);
        setModuleCatalogError(err?.message || 'Failed to load module catalog');
      })
      .finally(() => {
        if (!cancelled) setModuleCatalogLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const normalizeModuleEntries = useCallback((entries: any[] = []) => (
    entries
      .filter((entry) => entry && (typeof entry === 'string' || entry.code))
      .map((entry) => (
        typeof entry === 'string'
          ? { code: entry, isEnabled: true }
          : { ...entry, code: entry.code, isEnabled: entry.isEnabled !== false }
      ))
  ), []);

  const normalizedModules = useMemo(
    () => normalizeModuleEntries(modules),
    [modules, normalizeModuleEntries],
  );
  // Compare canonicalized codes on both sides: tenant documents may store
  // drifted kebab spellings (e.g. 'wildland') while the catalog renders
  // canonical codes ('wildlandFire') — same module either way.
  const activeModuleCodes = useMemo(() => new Set(
    normalizedModules
      .filter((m) => m.isEnabled !== false)
      .map((m) => canonicalizeTenantModuleCode(m.code)),
  ), [normalizedModules]);
  const moduleCatalogCodes = new Set(moduleCatalog.map((m: any) => canonicalizeTenantModuleCode(m.code)));
  const legacyModules = normalizedModules.filter((m) => !moduleCatalogCodes.has(canonicalizeTenantModuleCode(m.code)));

  const handleModuleToggle = useCallback((code: string, enabled: boolean) => {
    const catalogEntry = moduleCatalog.find((m: any) => m.code === code);
    // Deprecated modules cannot be newly enabled; disabling an enabled
    // deprecated module stays allowed so it can still be turned off.
    if (enabled && !canEnableModuleSelection(catalogEntry)) return;
    const targetCanonical = canonicalizeTenantModuleCode(code);
    if (!enabled) {
      const otherCatalogEnabled = moduleCatalog.some((m: any) =>
        canonicalizeTenantModuleCode(m.code) !== targetCanonical
        && activeModuleCodes.has(canonicalizeTenantModuleCode(m.code)),
      );
      if (!otherCatalogEnabled) return;
    }
    const next = buildToggledModules(modules, code, enabled, catalogEntry);

    // Refuse to leave the department with zero catalog-backed enabled modules.
    // Unavailable codes do not satisfy the selection invariant.
    const nextEnabledCanonical = new Set(
      next
        .filter((m: any) => m.isEnabled !== false)
        .map((m: any) => canonicalizeTenantModuleCode(m.code)),
    );
    const catalogStillEnabled = moduleCatalog.some((m: any) =>
      nextEnabledCanonical.has(canonicalizeTenantModuleCode(m.code)),
    );
    if (!catalogStillEnabled) return;

    onSave('modules', next);
  }, [modules, moduleCatalog, activeModuleCodes, onSave]);

  const handleLegacyModuleRemove = useCallback((code: string) => {
    const next = normalizeModuleEntries(modules).filter((m) => m.code !== code);
    // Removing the last enabled (legacy) module would leave the department
    // with zero enabled modules.
    if (!next.some((m) => m.isEnabled !== false)) return;
    onSave('modules', next);
  }, [modules, normalizeModuleEntries, onSave]);

  const isTrial = sub.status === 'trial' || sub.status === 'expired';
  const hasTrialData = isTrial || !!onboarding.status || !!trial.startDate;
  const trialExpired = trial.endDate ? new Date(trial.endDate) < new Date() : false;
  const trialDaysLeft = trial.endDate
    ? Math.max(0, Math.ceil((new Date(trial.endDate).getTime() - Date.now()) / 86400000))
    : 0;
  const isConverted = trial.outcome === 'converted';

  const visibleTabs = ALL_TABS.filter((t) => t.id !== 'trial' || hasTrialData);

  // ── Provisioning error banner state (Phase 4) ─────────────────────────────
  // Shown when the tenant is stuck in `status: 'provisioning'` with a recorded
  // `provisioningError`. The admin can click "Retry Provisioning" to re-run
  // the background steps.
  //
  // Auto-poll (10 s interval): when the tenant is in `status: 'provisioning'`
  // (with or without a provisioningError), refreshTenantFromServer() fetches
  // the server-authoritative record and writes it into Dexie. The watchTenants
  // liveQuery in TenantsPage detects the Dexie write and propagates the updated
  // tenant prop back here, clearing the banner when provisioning succeeds.
  // This ensures the banner disappears even when background sync is slow.
  const provisioningError = tenant.provisioningError as
    | { message: string; failedStep: 'database' | 'regrid_import' | 'code_set_downloads' | 'invite' | 'redirect_uris'; failedAt?: string | Date }
    | null
    | undefined;
  const provisioningProgress = (tenant.provisioningProgress ?? {}) as Record<string,
    | { status: 'pending' | 'running' | 'completed' | 'failed' | 'skipped'; reason?: string; error?: { message?: string }; startedAt?: string | Date; completedAt?: string | Date }
    | undefined>;
  const isStuckProvisioning = tenant.status === 'provisioning' && !!provisioningError;
  const isProvisioningInProgress = tenant.status === 'provisioning';
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [retryingRegrid, setRetryingRegrid] = useState(false);
  const [regridRetryError, setRegridRetryError] = useState<string | null>(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [statusReport, setStatusReport] = useState<{
    reconciled: boolean;
    steps: Record<string, { recorded?: string; actual: string; details?: string }>;
  } | null>(null);
  const [statusCheckError, setStatusCheckError] = useState<string | null>(null);
  const provisioningPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Auto-poll while tenant is in provisioning state.
  // Writes a fresh server record into Dexie on each tick, which triggers the
  // watchTenants liveQuery in the parent page to update the tenant prop here.
  useEffect(() => {
    if (!isProvisioningInProgress) {
      if (provisioningPollRef.current !== null) {
        clearInterval(provisioningPollRef.current);
        provisioningPollRef.current = null;
      }
      return;
    }

    // Fire an immediate check so the UI clears as soon as the panel opens if
    // the server has already completed provisioning. The report is also
    // surfaced to the statusReport state so the per-step verified view stays
    // current between manual "Check Status" clicks.
    const checkStatus = () => {
      checkProvisioningStatus(tenant._id)
        .then((report) => {
          setStatusReport({
            reconciled: report.reconciled,
            steps: report.steps,
          });
        })
        .catch((err) => {
          console.warn('[DepartmentPanel] Provisioning status check failed:', err);
        });
    };

    checkStatus();
    provisioningPollRef.current = setInterval(checkStatus, 10_000);

    return () => {
      if (provisioningPollRef.current !== null) {
        clearInterval(provisioningPollRef.current);
        provisioningPollRef.current = null;
      }
    };
  }, [isProvisioningInProgress, tenant._id]);

  // Clear stale status report when the tenant exits provisioning.
  useEffect(() => {
    if (!isProvisioningInProgress) {
      setStatusReport(null);
      setStatusCheckError(null);
    }
  }, [isProvisioningInProgress]);

  const handleRetryProvisioning = useCallback(async () => {
    setRetrying(true);
    setRetryError(null);
    try {
      await retryProvisioning(tenant._id);
    } catch (err: any) {
      setRetryError(err?.message || 'Retry failed');
    } finally {
      setRetrying(false);
    }
  }, [tenant._id]);

  const handleRetryRegridImport = useCallback(async () => {
    setRetryingRegrid(true);
    setRegridRetryError(null);
    try {
      await retryRegridImport(tenant._id);
    } catch (err: any) {
      setRegridRetryError(err?.message || 'Regrid retry failed');
    } finally {
      setRetryingRegrid(false);
    }
  }, [tenant._id]);

  const handleCheckProvisioningStatus = useCallback(async () => {
    setCheckingStatus(true);
    setStatusCheckError(null);
    try {
      const report = await checkProvisioningStatus(tenant._id);
      setStatusReport({
        reconciled: report.reconciled,
        steps: report.steps,
      });
    } catch (err: any) {
      console.warn('[DepartmentPanel] Manual status check failed:', err);
      setStatusCheckError(err?.message || 'Status check failed');
    } finally {
      setCheckingStatus(false);
    }
  }, [tenant._id]);

  const stepDisplayName: Record<string, string> = {
    database: 'Tenant database setup',
    regrid_import: 'Regrid parcel import',
    code_set_downloads: 'Code books download',
    admin_invite: 'Initial admin invitation',
    invite: 'Initial admin invitation',
    redirect_uris: 'SSO redirect URI registration',
  };
  const failedStepLabel = stepDisplayName;

  const orderedSteps = [
    { key: 'database', label: stepDisplayName.database },
    { key: 'regrid_import', label: stepDisplayName.regrid_import },
    { key: 'code_set_downloads', label: stepDisplayName.code_set_downloads },
    { key: 'admin_invite', label: stepDisplayName.admin_invite },
    { key: 'redirect_uris', label: stepDisplayName.redirect_uris },
  ];
  const hasProvisioningProgress = orderedSteps.some(({ key }) => provisioningProgress[key]?.status !== undefined);

  // G-006: Show the "Retry Regrid Import" button when the regrid_import step
  // is `failed` or `skipped`. This is a targeted retry that only re-enqueues
  // the Regrid ingestion job, unlike "Retry Provisioning" which re-runs all
  // steps.
  const regridStepStatus = provisioningProgress.regrid_import?.status;
  const canRetryRegrid = regridStepStatus === 'failed' || regridStepStatus === 'skipped';

  return (
    <div className="flex flex-col h-full" style={{ background: theme.cardBg, color: theme.textPrimary }}>
      {/* Provisioning error banner — shown when stuck with a recorded failure */}
      {isStuckProvisioning && provisioningError && (
        <div
          className="shrink-0 border-b px-4 py-3"
          style={{ background: '#fef2f2', borderColor: '#fecaca' }}
          role="alert"
        >
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-red-800">
                Provisioning failed: {failedStepLabel[provisioningError.failedStep] ?? provisioningError.failedStep}
              </p>
              <p className="text-xs text-red-700 mt-1 break-words">{provisioningError.message}</p>
              {provisioningError.failedAt && (
                <p className="text-xs text-red-600 mt-1">
                  Reported {new Date(provisioningError.failedAt).toLocaleString()}
                </p>
              )}
              {retryError && (
                <p className="text-xs text-red-700 mt-1">Retry request failed: {retryError}</p>
              )}
              {regridRetryError && (
                <p className="text-xs text-red-700 mt-1">Regrid retry failed: {regridRetryError}</p>
              )}
              {hasProvisioningProgress && (
                <div className="mt-2 space-y-1">
                  {orderedSteps.map(({ key, label }) => {
                    const step = provisioningProgress[key];
                    const status = step?.status ?? 'pending';
                    const statusText = status === 'completed' ? 'Completed' : status === 'running' ? 'Running' : status === 'failed' ? 'Failed' : status === 'skipped' ? 'Skipped' : 'Pending';
                    const color = status === 'completed' ? 'text-green-700' : status === 'failed' ? 'text-red-700' : status === 'skipped' ? 'text-gray-500' : 'text-red-600';
                    return (
                      <p key={key} className={`text-xs ${color}`}>
                        <span className="font-medium">{label}:</span> {statusText}
                      </p>
                    );
                  })}
                </div>
              )}
              {statusReport && (
                <div className="mt-2 pt-2 border-t border-red-200">
                  <p className="text-xs font-semibold text-red-800 mb-1">
                    {statusReport.reconciled
                      ? 'Verified complete — reconciled to active'
                      : 'Actively verified status:'}
                  </p>
                  {orderedSteps.map(({ key, label }) => {
                    const step = statusReport.steps[key];
                    if (!step) return null;
                    const actual = step.actual;
                    const statusText = actual === 'complete' ? 'Verified complete'
                      : actual === 'incomplete' ? 'Not complete'
                      : actual === 'not_applicable' ? 'N/A'
                      : 'Unknown';
                    const color = actual === 'complete' ? 'text-green-700'
                      : actual === 'incomplete' ? 'text-red-700'
                      : 'text-red-600';
                    return (
                      <p key={key} className={`text-xs ${color}`}>
                        <span className="font-medium">{label}:</span> {statusText}
                        {step.details && <span className="text-red-500"> — {step.details}</span>}
                      </p>
                    );
                  })}
                </div>
              )}
              {statusCheckError && (
                <p className="text-xs text-red-700 mt-1">Status check failed: {statusCheckError}</p>
              )}
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              <button
                type="button"
                onClick={handleRetryProvisioning}
                disabled={retrying || checkingStatus}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {retrying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                {retrying ? 'Retrying…' : 'Retry Provisioning'}
              </button>
              <button
                type="button"
                onClick={handleCheckProvisioningStatus}
                disabled={retrying || checkingStatus}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-red-300 text-red-700 bg-white hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkingStatus ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                {checkingStatus ? 'Checking…' : 'Check Status'}
              </button>
              {canRetryRegrid && (
                <button
                  type="button"
                  onClick={handleRetryRegridImport}
                  disabled={retryingRegrid || retrying || checkingStatus}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {retryingRegrid ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  {retryingRegrid ? 'Retrying Regrid…' : 'Retry Regrid Import'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Provisioning in-progress banner — shown while provisioning is running (no recorded error) */}
      {isProvisioningInProgress && !isStuckProvisioning && (
        <div
          className="shrink-0 border-b px-4 py-3"
          style={{ background: '#fffbeb', borderColor: '#fde68a' }}
          role="status"
        >
          <div className="flex items-center gap-3">
            <Loader2 className="h-5 w-5 text-amber-600 shrink-0 animate-spin" aria-hidden />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-amber-800">Provisioning in progress…</p>
              <p className="text-xs text-amber-700 mt-0.5">
                Background setup is running. This panel will update automatically when complete.
              </p>
              {hasProvisioningProgress && (
                <div className="mt-2 space-y-1">
                  {orderedSteps.map(({ key, label }) => {
                    const step = provisioningProgress[key];
                    const status = step?.status ?? 'pending';
                    const statusText = status === 'completed' ? 'Completed' : status === 'running' ? 'Running' : status === 'failed' ? 'Failed' : status === 'skipped' ? 'Skipped' : 'Pending';
                    const color = status === 'completed' ? 'text-green-700' : status === 'running' ? 'text-amber-800' : status === 'skipped' ? 'text-gray-500' : 'text-amber-700';
                    return (
                      <p key={key} className={`text-xs ${color}`}>
                        <span className="font-medium">{label}:</span> {statusText}
                      </p>
                    );
                  })}
                </div>
              )}
              {statusReport && (
                <div className="mt-2 pt-2 border-t border-amber-200">
                  <p className="text-xs font-semibold text-amber-800 mb-1">
                    {statusReport.reconciled
                      ? 'Verified complete — reconciled to active'
                      : 'Actively verified status:'}
                  </p>
                  {orderedSteps.map(({ key, label }) => {
                    const step = statusReport.steps[key];
                    if (!step) return null;
                    const actual = step.actual;
                    const statusText = actual === 'complete' ? 'Verified complete'
                      : actual === 'incomplete' ? 'Not complete'
                      : actual === 'not_applicable' ? 'N/A'
                      : 'Unknown';
                    const color = actual === 'complete' ? 'text-green-700'
                      : actual === 'incomplete' ? 'text-red-700'
                      : 'text-amber-700';
                    return (
                      <p key={key} className={`text-xs ${color}`}>
                        <span className="font-medium">{label}:</span> {statusText}
                        {step.details && <span className="text-amber-600"> — {step.details}</span>}
                      </p>
                    );
                  })}
                </div>
              )}
              {statusCheckError && (
                <p className="text-xs text-red-700 mt-1">Status check failed: {statusCheckError}</p>
              )}
              {regridRetryError && (
                <p className="text-xs text-red-700 mt-1">Regrid retry failed: {regridRetryError}</p>
              )}
            </div>
            <div className="flex flex-col gap-1.5 shrink-0">
              <button
                type="button"
                onClick={handleCheckProvisioningStatus}
                disabled={checkingStatus || retryingRegrid}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-amber-300 text-amber-700 bg-white hover:bg-amber-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {checkingStatus ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                {checkingStatus ? 'Checking…' : 'Check Status'}
              </button>
              {canRetryRegrid && (
                <button
                  type="button"
                  onClick={handleRetryRegridImport}
                  disabled={retryingRegrid || checkingStatus}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {retryingRegrid ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
                  {retryingRegrid ? 'Retrying Regrid…' : 'Retry Regrid Import'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex shrink-0 overflow-x-auto" style={{ borderBottom: `1px solid ${theme.cardBorder}` }}>
        {visibleTabs.map((t) => (
          <button
            key={t.id}
            onClick={() => onTabChange(t.id)}
            className="px-3 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors"
            style={{
              borderBottomColor: activeTab === t.id ? theme.accent : 'transparent',
              color: activeTab === t.id ? theme.accentText : theme.textSecondary,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-y-auto p-4">

        {/* ── Overview ── */}
        {activeTab === 'overview' && (
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-4">
              {tenant.tier && (
                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full capitalize ${TIER_COLORS[tenant.tier] || 'bg-gray-100 text-gray-600'}`}>
                  {tenant.tier}
                </span>
              )}
              {tenant.status && (
                <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${STATUS_COLORS[tenant.status] || 'bg-gray-100 text-gray-600'}`}>
                  {tenant.status}
                </span>
              )}
              {isConverted && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                  <Sparkles className="h-3 w-3" /> Converted
                </span>
              )}
            </div>

            <PanelSection title="Department Info" icon={Building2} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <InlineEditableField label="Name" value={tenant.name || ''} config={cfg('name', 'text', { validate: (v) => (v as string)?.trim() ? null : 'Required' })} onSave={onSave} />
                <ReadField label="Slug" value={tenant.slug} mono textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                <InlineEditableField label="Department Type" value={tenant.departmentType || ''} config={cfg('departmentType', 'text')} onSave={onSave} />
                <InlineEditableField label="Classification" value={tenant.departmentClassification || ''} config={cfg('departmentClassification', 'text')} onSave={onSave} />
                <InlineEditableField label="Timezone" value={tenant.timezone || ''} config={cfg('timezone', 'text')} onSave={onSave} />
                <InlineEditableField label="Website" value={tenant.website || ''} config={cfg('website', 'text', { validate: validateWebsite, placeholder: 'Enter department website' })} onSave={onSave} />
                <InlineEditableField label="Phone" value={tenant.phone ? formatPhone(tenant.phone) : ''} config={cfg('phone', 'tel')} onSave={onSave} />
                <InlineEditableField label="Contact Email" value={tenant.contactEmail || ''} config={cfg('contactEmail', 'email')} onSave={onSave} />
              </dl>
            </PanelSection>

            <PanelSection title="Address" icon={MapPin} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <InlineEditableField label="Street" value={addr.street || ''} config={cfg('address.street', 'text')} onSave={onSave} span={2} />
                <InlineEditableField label="City" value={(typeof jurisdiction.city === 'string' ? jurisdiction.city : jurisdiction.city?.name) || ''} config={cfg('address.city', 'text')} onSave={onSave} />
                <JurisdictionStateCountyField
                  jurisdiction={jurisdiction}
                  onSave={(next) => void onSave('jurisdiction', next)}
                />
                <InlineEditableField label="ZIP" value={addr.zip || ''} config={cfg('address.zip', 'text', { validate: validateZip, placeholder: '95603' })} onSave={onSave} />
                <JurisdictionCountryField
                  jurisdiction={jurisdiction}
                  onSave={(next) => void onSave('jurisdiction', next)}
                />
              </dl>
            </PanelSection>

            <PanelSection title="Jurisdiction" icon={Globe} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <JurisdictionCountryField
                  jurisdiction={jurisdiction}
                  onSave={(next) => void onSave('jurisdiction', next)}
                />
                <JurisdictionStateCountyField
                  jurisdiction={jurisdiction}
                  onSave={(next) => void onSave('jurisdiction', next)}
                />
                <InlineEditableField label="City" value={(typeof jurisdiction.city === 'string' ? jurisdiction.city : jurisdiction.city?.name) || ''} config={cfg('jurisdiction.city', 'text')} onSave={onSave} />
              </dl>
            </PanelSection>

            <PanelSection title="Metadata" icon={Clock} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <ReadField label="Tenant ID" value={tenant._id} mono textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                <ReadField label="Organization ID" value={tenant.organizationId} mono textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                <ReadField label="Created" value={tenant.createdAt ? new Date(tenant.createdAt).toLocaleString() : undefined} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                <ReadField label="Updated" value={tenant.updatedAt ? new Date(tenant.updatedAt).toLocaleString() : undefined} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
              </dl>
            </PanelSection>
          </div>
        )}

        {/* ── Billing & Plan ── */}
        {activeTab === 'billing' && (
          <div>
            <PanelSection title="Billing" icon={CreditCard} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <InlineEditableField label="Billing Entity" value={billing.billingEntity || ''} config={cfg('billing.billingEntity', 'text')} onSave={onSave} />
                <ReadField label="Subscription Status" value={sub.status} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                <ReadField label="Start Date" value={sub.startDate ? new Date(sub.startDate).toLocaleDateString() : undefined} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                <InlineEditableField label="Renewal Date" value={sub.renewalDate || ''} editValue={sub.renewalDate || ''} config={cfg('billing.subscription.renewalDate', 'date')} onSave={onSave} />
                <InlineEditableField label="Auto-Renew" value={sub.autoRenew !== undefined ? (sub.autoRenew ? 'Yes' : 'No') : '—'} editValue={sub.autoRenew ?? false} config={cfg('billing.subscription.autoRenew', 'boolean')} onSave={onSave} />
                <InlineEditableField label="Billing Cycle" value={pricing.billingCycle || ''} config={cfg('billing.pricing.billingCycle', 'select', { options: [{ value: 'monthly', label: 'Monthly' }, { value: 'annual', label: 'Annual' }] })} onSave={onSave} />
                <InlineEditableField label="Amount" value={pricing.contractedAmount != null ? `$${Number(pricing.contractedAmount).toLocaleString()} ${pricing.currency || 'USD'}` : '—'} editValue={pricing.contractedAmount ?? 0} config={cfg('billing.pricing.contractedAmount', 'number', { numberConfig: { min: 0, step: 1 } })} onSave={onSave} />
                <InlineEditableField label="Billing Contact" value={billing.billingContact?.name || ''} config={cfg('billing.billingContact.name', 'text')} onSave={onSave} />
                <InlineEditableField label="Billing Email" value={billing.billingContact?.email || ''} config={cfg('billing.billingContact.email', 'email')} onSave={onSave} />
                <ReadField label="Fiscal Year Start" value={tenant.fiscalYear ? `Month ${tenant.fiscalYear.startMonth}, Day ${tenant.fiscalYear.startDay}` : undefined} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
              </dl>

              {!isConverted && (sub.status === 'trial' || sub.status === 'expired') && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-amber-900">Trial status is still applied to this department</p>
                      <p className="mt-1 text-xs text-amber-800">
                        Sales can extend the trial here or activate the department to move it to a normal paying subscription.
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <button
                        type="button"
                        onClick={() => onExtend(tenant._id, tenant.name)}
                        className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                      >
                        Extend Trial
                      </button>
                      <button
                        type="button"
                        onClick={() => onConvert(tenant._id, tenant.name)}
                        className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-700 rounded-lg hover:bg-green-100 transition-colors"
                      >
                        Activate Department
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </PanelSection>

            <PanelSection title="Inspection Plan" icon={BarChart3} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <InlineEditableField label="Volume Band" value={plan.volumeBand || ''} config={cfg('inspectionPlan.volumeBand', 'text')} onSave={onSave} />
                <InlineEditableField label="Max Series/Year" value={plan.maxSeriesPerYear != null ? Number(plan.maxSeriesPerYear).toLocaleString() : '—'} editValue={plan.maxSeriesPerYear ?? 0} config={cfg('inspectionPlan.maxSeriesPerYear', 'number', { numberConfig: { min: 0, step: 1 } })} onSave={onSave} />
                <InlineEditableField label="Commitment Term" value={plan.commitmentTerm || ''} config={cfg('inspectionPlan.commitmentTerm', 'select', { options: [{ value: '1-year', label: '1 Year' }, { value: '2-year', label: '2 Years' }, { value: '3-year', label: '3 Years' }, { value: 'month-to-month', label: 'Month-to-Month' }] })} onSave={onSave} />
                <InlineEditableField label="Commitment Discount" value={plan.commitmentDiscount != null ? `${plan.commitmentDiscount}%` : '—'} editValue={plan.commitmentDiscount ?? 0} config={cfg('inspectionPlan.commitmentDiscount', 'number', { numberConfig: { min: 0, max: 100, step: 1, suffix: '%' } })} onSave={onSave} />
                <InlineEditableField label="Annual Price" value={plan.annualPrice != null ? `$${Number(plan.annualPrice).toLocaleString()}` : '—'} editValue={plan.annualPrice ?? 0} config={cfg('inspectionPlan.annualPrice', 'number', { numberConfig: { min: 0, step: 1 } })} onSave={onSave} />
                <InlineEditableField label="Overage Policy" value={plan.overagePolicy || ''} config={cfg('inspectionPlan.overagePolicy', 'select', { options: [{ value: 'block', label: 'Block' }, { value: 'charge', label: 'Charge' }, { value: 'notify', label: 'Notify' }] })} onSave={onSave} />
                <InlineEditableField label="Overage Rate" value={plan.overageRate != null ? `$${plan.overageRate}/series` : '—'} editValue={plan.overageRate ?? 0} config={cfg('inspectionPlan.overageRate', 'number', { numberConfig: { min: 0, step: 0.01 } })} onSave={onSave} />
                <ReadField label="Source" value={plan.source} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
              </dl>
            </PanelSection>

            <PanelSection title="Licensing" icon={Users} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <ReadField label="Source" value={licensing.source} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                <InlineEditableField label="Admin Seats" value={licensing.seats?.admin?.purchased != null ? String(licensing.seats.admin.purchased) : '—'} editValue={licensing.seats?.admin?.purchased ?? 0} config={cfg('licensing.seats.admin.purchased', 'number', { numberConfig: { min: 0, step: 1 } })} onSave={onSave} />
                <InlineEditableField label="Non-Admin Seats" value={licensing.seats?.nonAdmin?.purchased != null ? String(licensing.seats.nonAdmin.purchased) : '—'} editValue={licensing.seats?.nonAdmin?.purchased ?? 0} config={cfg('licensing.seats.nonAdmin.purchased', 'number', { numberConfig: { min: 0, step: 1 } })} onSave={onSave} />
              </dl>
            </PanelSection>
          </div>
        )}

        {/* ── Configuration ── */}
        {activeTab === 'config' && (
          <div>
            <DepartmentAuthManagementCard tenantId={tenant._id} departmentName={tenant.name} onOpenWizard={() => setAuthWizardOpen(true)} />
            {authWizardOpen && <DepartmentAuthSetupWizard tenantId={tenant._id} departmentName={tenant.name} onClose={() => setAuthWizardOpen(false)} />}

            <PanelSection title="Branding" icon={Palette} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
              <p className="text-xs mb-3" style={{ color: theme.textSecondary }}>
                Header logos. The primary logo fills the left slot and the secondary logo the right slot when each is enabled on a report.
              </p>
              <div className="space-y-4 mb-4">
                <DepartmentLogoUploadField
                  label="Primary logo (left)"
                  value={brand.logo}
                  onChange={(ref) => { void onSave('branding.logo', ref); }}
                />
                <DepartmentLogoUploadField
                  label="Secondary logo (right)"
                  value={brand.secondLogo}
                  onChange={(ref) => { void onSave('branding.secondLogo', ref); }}
                />
              </div>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3 mb-3">
                <InlineEditableField label="Login Background" value={brand.loginBackgroundUrl || ''} config={cfg('branding.loginBackgroundUrl', 'text')} onSave={onSave} span={2} />
                <InlineEditableField label="Background Opacity" value={brand.backgroundOpacity != null ? `${Math.round(brand.backgroundOpacity * 100)}%` : '—'} editValue={brand.backgroundOpacity != null ? Math.round(brand.backgroundOpacity * 100) : 0} config={cfg('branding.backgroundOpacity', 'number', { numberConfig: { min: 0, max: 100, step: 1, suffix: '%' } })} onSave={onSave} />
              </dl>
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded border border-gray-200 shrink-0" style={{ backgroundColor: brand.primaryColor || '#B22222' }} />
                  <span className="text-xs" style={{ color: theme.textSecondary }}>Primary: <span className="font-mono">{brand.primaryColor || '—'}</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="h-6 w-6 rounded border border-gray-200 shrink-0" style={{ backgroundColor: brand.secondaryColor || '#1C1C1C' }} />
                  <span className="text-xs" style={{ color: theme.textSecondary }}>Secondary: <span className="font-mono">{brand.secondaryColor || '—'}</span></span>
                </div>
              </div>
            </PanelSection>

            <PanelSection title="Notification Preferences" icon={Bell} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <InlineEditableField label="Email Digest" value={notif.emailDigest || ''} config={cfg('notificationPreferences.emailDigest', 'select', { options: [{ value: 'daily', label: 'Daily' }, { value: 'weekly', label: 'Weekly' }, { value: 'none', label: 'None' }] })} onSave={onSave} />
                <InlineEditableField label="Trial Expiry Alert" value={notif.trialExpiryAlertDays != null ? `${notif.trialExpiryAlertDays} days before` : '—'} editValue={notif.trialExpiryAlertDays ?? 0} config={cfg('notificationPreferences.trialExpiryAlertDays', 'number', { numberConfig: { min: 0, step: 1 } })} onSave={onSave} />
                <InlineEditableField label="Volume Threshold" value={notif.volumeThresholdPercent != null ? `${notif.volumeThresholdPercent}%` : '—'} editValue={notif.volumeThresholdPercent ?? 0} config={cfg('notificationPreferences.volumeThresholdPercent', 'number', { numberConfig: { min: 0, max: 100, step: 1, suffix: '%' } })} onSave={onSave} />
              </dl>
            </PanelSection>

            <PanelSection title="Data Retention" icon={Clock} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <InlineEditableField label="Inspections" value={retention.inspectionRetentionYears != null ? `${retention.inspectionRetentionYears} yrs` : '—'} editValue={retention.inspectionRetentionYears ?? 0} config={cfg('dataRetentionPolicy.inspectionRetentionYears', 'number', { numberConfig: { min: 0, step: 1, suffix: ' yrs' } })} onSave={onSave} />
                <InlineEditableField label="Photos" value={retention.photoRetentionYears != null ? `${retention.photoRetentionYears} yrs` : '—'} editValue={retention.photoRetentionYears ?? 0} config={cfg('dataRetentionPolicy.photoRetentionYears', 'number', { numberConfig: { min: 0, step: 1, suffix: ' yrs' } })} onSave={onSave} />
                <InlineEditableField label="Documents" value={retention.documentRetentionYears != null ? `${retention.documentRetentionYears} yrs` : '—'} editValue={retention.documentRetentionYears ?? 0} config={cfg('dataRetentionPolicy.documentRetentionYears', 'number', { numberConfig: { min: 0, step: 1, suffix: ' yrs' } })} onSave={onSave} />
                <InlineEditableField label="Reports" value={retention.reportRetentionYears != null ? `${retention.reportRetentionYears} yrs` : '—'} editValue={retention.reportRetentionYears ?? 0} config={cfg('dataRetentionPolicy.reportRetentionYears', 'number', { numberConfig: { min: 0, step: 1, suffix: ' yrs' } })} onSave={onSave} />
                <InlineEditableField label="Audit Logs" value={retention.auditLogRetentionYears != null ? `${retention.auditLogRetentionYears} yrs` : '—'} editValue={retention.auditLogRetentionYears ?? 0} config={cfg('dataRetentionPolicy.auditLogRetentionYears', 'number', { numberConfig: { min: 0, step: 1, suffix: ' yrs' } })} onSave={onSave} />
                <InlineEditableField label="Deletion Policy" value={retention.deletionPolicy || ''} config={cfg('dataRetentionPolicy.deletionPolicy', 'select', { options: [{ value: 'soft', label: 'Soft' }, { value: 'hard', label: 'Hard' }, { value: 'archive', label: 'Archive' }] })} onSave={onSave} />
              </dl>
            </PanelSection>

            <PanelSection title="Data Location" icon={Database} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                <ReadField label="Database Name" value={tenant.dataLocation?.databaseName} mono textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                <ReadField label="Cluster" value={clusterName} mono textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                <ReadField label="Region" value={tenant.dataLocation?.region} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
              </dl>
            </PanelSection>
          </div>
        )}

        {/* ── Mapping ── */}
        {activeTab === 'mapping' && (
          <DepartmentMappingTab tenant={tenant} onSave={onSave} />
        )}

        {/* ── Modules & Features ── */}
        {activeTab === 'modules' && (
          <div>
            <div className="mt-4" />

            <PanelSection title="Modules" icon={Settings} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
              {moduleCatalogLoading ? (
                <div className="py-6 text-sm text-gray-500">Loading module catalog…</div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm" style={{ color: theme.textSecondary }}>
                        Toggle the modules this department can use. Changes save immediately through the local-first department record. At least one module must stay enabled — the last enabled module cannot be unselected.
                      </p>
                      {moduleCatalogError && (
                        <p className="mt-1 text-xs text-red-600">{moduleCatalogError}</p>
                      )}
                    </div>
                    <span className="text-xs px-2 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-200 shrink-0">
                      {activeModuleCodes.size} enabled
                    </span>
                  </div>

                  {moduleCatalog.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {moduleCatalog.map((module: any) => {
                        const moduleCanonical = canonicalizeTenantModuleCode(module.code);
                        const checked = activeModuleCodes.has(moduleCanonical);
                        const statusLabel = module.status || 'available';
                        // The last *catalog-backed* enabled module cannot be
                        // unchecked. Unavailable codes (legacy / missing from
                        // the catalog) do not count — counting them lets the
                        // sole catalog module be cleared, which server
                        // validation then rejects as an empty selection.
                        const otherCatalogEnabled = moduleCatalog.some((m: any) =>
                          canonicalizeTenantModuleCode(m.code) !== moduleCanonical
                          && activeModuleCodes.has(canonicalizeTenantModuleCode(m.code)),
                        );
                        const isLastEnabled = checked && !otherCatalogEnabled;
                        // A deprecated module cannot be newly enabled; an
                        // enabled deprecated module can still be disabled.
                        const isDeprecatedLocked = !checked && !canEnableModuleSelection(module);
                        const isModuleLocked = isLastEnabled || isDeprecatedLocked;

                        return (
                          <label
                            key={module._id || module.code}
                            className="flex items-start gap-3 rounded-lg border p-3 transition-colors"
                            style={{
                              background: checked ? theme.expandedBg : theme.cardBg,
                              borderColor: checked ? theme.accent : theme.expandedCardBorder,
                              cursor: isModuleLocked ? 'not-allowed' : 'pointer',
                            }}
                            title={isDeprecatedLocked
                              ? 'Module is deprecated — cannot be enabled'
                              : isLastEnabled ? 'At least one module must stay enabled' : undefined}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              disabled={isModuleLocked}
                              onChange={(e) => handleModuleToggle(module.code, e.target.checked)}
                              className="mt-1 h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500 disabled:cursor-not-allowed"
                            />
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="text-sm font-semibold" style={{ color: theme.textPrimary }}>
                                  {module.name || module.code}
                                </span>
                                <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">
                                  {module.code}
                                </span>
                                {module.status && module.status !== 'ga' && module.status !== 'active' && (
                                  <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                                    {statusLabel}
                                  </span>
                                )}
                              </div>
                              {module.description && (
                                <p className="mt-1 text-xs" style={{ color: theme.textSecondary }}>
                                  {module.description}
                                </p>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="py-4 text-sm text-gray-500">No module catalog entries found.</div>
                  )}

                  {legacyModules.length > 0 && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                        <h4 className="text-sm font-semibold text-amber-900">Legacy / invalid modules</h4>
                      </div>
                      <p className="text-xs text-amber-800 mb-3">
                        These module codes are on the department record but no longer exist in the current module catalog.
                        Remove them after confirming they are no longer needed.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {legacyModules.map((m) => (
                          <span
                            key={m.code}
                            className="inline-flex items-center gap-2 rounded-full border border-amber-300 bg-white px-3 py-1 text-xs text-amber-900"
                          >
                            <span className="font-mono">{m.code}</span>
                            <button
                              type="button"
                              onClick={() => handleLegacyModuleRemove(m.code)}
                              className="rounded-full px-2 py-0.5 text-[11px] font-medium text-amber-700 hover:bg-amber-100"
                            >
                              Remove
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </PanelSection>

            {reg.primaryFireCode && (
              <PanelSection title="Regulatory Framework" icon={FileText} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <InlineEditableField label="Primary Fire Code" value={reg.primaryFireCode || ''} config={cfg('regulatoryFramework.primaryFireCode', 'text')} onSave={onSave} />
                  <InlineEditableField label="Code Edition" value={reg.codeEdition || ''} config={cfg('regulatoryFramework.codeEdition', 'text')} onSave={onSave} />
                  <ReadField label="Adopted Amendments" value={Array.isArray(reg.adoptedAmendments) && reg.adoptedAmendments.length > 0 ? `${reg.adoptedAmendments.length} amendments` : 'None'} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                </dl>
              </PanelSection>
            )}

            {legacy.isLinked && (
              <PanelSection title="Legacy System" icon={Database} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <ReadField label="Linked" value="Yes" textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  <ReadField label="Database Server" value={legacy.databaseServer} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  <ReadField label="Department ID" value={legacy.departmentId} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  <ReadField label="Last Synced" value={legacy.lastSyncedAt ? new Date(legacy.lastSyncedAt).toLocaleString() : undefined} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                </dl>
              </PanelSection>
            )}

            {documents.length > 0 && (
              <PanelSection title="Documents" icon={FileText} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
                <div className="space-y-2">
                  {documents.map((doc: any, i: number) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded" style={{ background: theme.cardBg, border: `1px solid ${theme.expandedCardBorder}` }}>
                      <div>
                        <span className="text-sm" style={{ color: theme.textPrimary }}>{doc.name}</span>
                        {doc.type && <span className="text-xs ml-2" style={{ color: theme.textMuted }}>{doc.type}</span>}
                      </div>
                      {doc.sizeBytes != null && (
                        <span className="text-xs text-gray-400">{(doc.sizeBytes / 1024).toFixed(1)} KB</span>
                      )}
                    </div>
                  ))}
                </div>
              </PanelSection>
            )}

            {modules.length === 0 && !reg.primaryFireCode && !legacy.isLinked && documents.length === 0 && (
              <div className="text-center py-10 text-gray-400 text-sm">No module data available</div>
            )}
          </div>
        )}

        {/* ── Contacts ── */}
        {activeTab === 'contacts' && (
          <DepartmentContactsTab
            contacts={contacts}
            onContactsChange={(updated: DeptContact[]) => {
              const rawById = new globalThis.Map(rawContacts.map((c: any) => [String(c?._id ?? ''), c]));
              return onSave('contacts', updated.map((c) => toStoredContact(c, rawById.get(c._id))));
            }}
            theme={theme}
          />
        )}

        {/* ── Code Sets ── */}
        {activeTab === 'codeSets' && (
          <DepartmentCodeSetsTab
            tenant={tenant}
            theme={theme}
            onSave={onSave}
          />
        )}

        {/* ── Documents ── */}
        {activeTab === 'documents' && (
          <DepartmentDocumentsTab tenant={tenant} theme={theme} />
        )}

        {/* ── Parcel Data (Regrid) ── */}
        {activeTab === 'parcelData' && (
          <DepartmentParcelDataTab tenant={tenant} />
        )}

        {/* ── Trial / Onboarding ── */}
        {activeTab === 'trial' && (
          <div>
            {hasTrialData && (
              <PanelSection title="Trial" icon={Clock} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 mb-3">
                  <ReadField label="Start Date" value={trial.startDate ? new Date(trial.startDate).toLocaleDateString() : undefined} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  <InlineEditableField label="End Date" value={trial.endDate ? new Date(trial.endDate).toLocaleDateString() : '—'} editValue={trial.endDate || ''} config={cfg('trial.endDate', 'date')} onSave={onSave} />
                  <ReadField label="Duration (days)" value={trial.durationDays} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  <ReadField label="Days Remaining" value={trial.endDate ? (trialExpired ? 'Expired' : `${trialDaysLeft} days`) : undefined} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  <InlineEditableField label="Support Level" value={trial.supportLevel || ''} config={cfg('trial.supportLevel', 'select', { options: [{ value: 'basic', label: 'Basic' }, { value: 'standard', label: 'Standard' }, { value: 'premium', label: 'Premium' }] })} onSave={onSave} />
                  <ReadField label="Outcome" value={trial.outcome} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  <ReadField label="Extensions" value={Array.isArray(trial.extensions) ? trial.extensions.length : 0} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  {trial.departmentContact && (
                    <>
                      <ReadField label="Dept Contact" value={trial.departmentContact.name} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                      <ReadField label="Dept Contact Email" value={trial.departmentContact.email} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                    </>
                  )}
                  {trial.salesNotes !== undefined && <InlineEditableField label="Sales Notes" value={trial.salesNotes || ''} config={cfg('trial.salesNotes', 'textarea')} onSave={onSave} span={2} />}
                </dl>

                {/* Trial action buttons */}
                {!isConverted && (
                  <div className="flex items-center gap-2 pt-2" style={{ borderTop: `1px solid ${theme.cardBorder}` }}>
                    {!trialExpired && (
                      <>
                        <button
                          onClick={() => onExtend(tenant._id, tenant.name)}
                          className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          Extend Trial
                        </button>
                        <button
                          onClick={() => onConvert(tenant._id, tenant.name)}
                          className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-700 rounded-lg hover:bg-green-100 transition-colors"
                        >
                          Activate Department
                        </button>
                      </>
                    )}
                    {trialExpired && (
                      <>
                        <button
                          onClick={() => onExtend(tenant._id, tenant.name)}
                          className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                        >
                          Extend Trial
                        </button>
                        <button
                          onClick={() => onConvert(tenant._id, tenant.name)}
                          className="px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-700 rounded-lg hover:bg-green-100 transition-colors"
                        >
                          Activate Department
                        </button>
                        <button
                          onClick={() => onCleanup(tenant._id, tenant.name)}
                          className="px-3 py-1.5 text-xs font-medium text-red-700 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                        >
                          Cleanup
                        </button>
                      </>
                    )}
                  </div>
                )}
              </PanelSection>
            )}

            {onboarding.status && (
              <PanelSection title="Onboarding" icon={Flame} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3">
                  <ReadField label="Status" value={onboarding.status} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  <ReadField label="Last Step" value={onboarding.lastStep} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  <ReadField label="Started" value={onboarding.startedAt ? new Date(onboarding.startedAt).toLocaleDateString() : undefined} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  <ReadField label="Completed" value={onboarding.completedAt ? new Date(onboarding.completedAt).toLocaleDateString() : undefined} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  <ReadField label="Initial Admin" value={onboarding.initialAdminName ? `${onboarding.initialAdminName}${onboarding.initialAdminEmail ? ` (${onboarding.initialAdminEmail})` : ''}` : onboarding.initialAdminEmail} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                  <ReadField label="Initial Admin Invite" value={onboarding.initialAdminInviteDeferred ? 'Deferred for later send' : 'Send immediately'} textPrimary={theme.textPrimary} textSecondary={theme.textSecondary} />
                </dl>
              </PanelSection>
            )}

            {!hasTrialData && sub.status === 'active' && onMarkAsTrial && (
              <PanelSection title="Convert to Trial" icon={Clock} expandedBg={theme.expandedBg} textSecondary={theme.textSecondary}>
                <p className="text-xs mb-3" style={{ color: theme.textSecondary }}>
                  This is currently an active department. You can convert it to trial mode for testing or sales purposes.
                </p>
                <button
                  onClick={() => onMarkAsTrial(tenant._id, tenant.name)}
                  className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  Mark as Trial
                </button>
              </PanelSection>
            )}
            
            {!hasTrialData && sub.status !== 'active' && (
              <div className="text-center py-10 text-gray-400 text-sm">No trial or onboarding data</div>
            )}
          </div>
        )}

        {/* ── Audit Log ── */}
        {activeTab === 'audit' && (
          <div className="px-4 py-3" style={{ background: theme.cardBg }}>
            <AuditLogTimeline
              documentId={tenant._id}
              collection="tenants"
              theme={theme}
              active={activeTab === 'audit'}
              fetchFn={fetchAuditLogs}
            />
          </div>
        )}

      </div>
    </div>
  );
}
